import { useEffect, useRef, useState } from "react";
import { LockKeyhole, Mail, ShieldCheck } from "lucide-react";
import { api, setCsrfToken } from "../api";
import { startWorksSso } from "../sso";
import { Button, FormField } from "../components/ui";
import { LoginTurnstile } from "../components/login-turnstile";
import type { CurrentUser } from "../types";

function WindowsMark() {
  return (
    <svg
      aria-hidden="true"
      className="windows-mark"
      viewBox="0 0 20 20"
      focusable="false"
    >
      <path d="M1 3.2 9 2v7H1V3.2Zm9 6.7V2l9-1.3V9.9h-9ZM1 10.2h8v7L1 16V10.2Zm9 0h9v9.1L10 18v-7.8Z" />
    </svg>
  );
}

export function LoginScreen({
  onAuthenticated,
}: {
  onAuthenticated(user: CurrentUser): void;
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);
  const inFlight = useRef(false);
  const [turnstileSiteKey, setTurnstileSiteKey] = useState("");
  const [turnstileRequired, setTurnstileRequired] = useState(false);
  const [turnstileLoaded, setTurnstileLoaded] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileResetNonce, setTurnstileResetNonce] = useState(0);

  useEffect(() => {
    let active = true;
    void api<{ siteKey: string; required: boolean }>("/auth/turnstile")
      .then((config) => {
        if (!active) return;
        if (typeof config?.siteKey !== "string" || typeof config?.required !== "boolean") throw new Error("invalid config");
        setTurnstileSiteKey(config.siteKey || "");
        setTurnstileRequired(Boolean(config.required));
        setTurnstileLoaded(true);
      })
      .catch(() => {
        if (active) {
          setTurnstileRequired(true);
          setTurnstileLoaded(true);
        }
      });
    return () => { active = false; };
  }, []);

  async function login(event: React.FormEvent) {
    event.preventDefault();
    if (inFlight.current) return;
    setError("");
    if (!turnstileLoaded) {
      setError("Đang tải xác minh bảo mật. Vui lòng thử lại sau ít giây.");
      return;
    }
    if (turnstileRequired && (!turnstileSiteKey || !turnstileToken)) {
      setError("Vui lòng hoàn tất xác minh bảo mật trước khi đăng nhập.");
      return;
    }
    setPending(true);
    inFlight.current = true;
    try {
      const result = await api<{ user: CurrentUser; csrfToken: string }>(
        "/auth/login",
        { method: "POST", body: JSON.stringify({ email, password, turnstileToken }) },
      );
      setCsrfToken(result.csrfToken);
      onAuthenticated({ ...result.user, csrfToken: result.csrfToken });
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Đăng nhập không thành công.",
      );
    } finally {
      if (turnstileRequired) {
        setTurnstileToken("");
        setTurnstileResetNonce((current) => current + 1);
      }
      setPending(false);
      inFlight.current = false;
    }
  }

  return (
    <div className="login-page">
      <div className="login-bg" />
      <main className="login-card">
        <div className="login-brand">
        <img src="/assets/vinh-hoan-logo.png?v=20260810" alt="Logo doanh nghiệp" />
          <div>
            <strong>BOM System</strong>
            <span>Hệ thống quản lý BOM của Phòng Kế hoạch</span>
          </div>
        </div>
        <h1>Đăng nhập</h1>
        <p>Truy cập hệ thống quản lý BOM của Phòng Kế hoạch.</p>
        <Button
          type="button"
          variant="outline"
          className="login-button sso-button"
          onClick={startWorksSso}
        >
          <WindowsMark /> Đăng nhập với Microsoft
        </Button>
        <div className="login-divider">
          <span>hoặc đăng nhập bằng tài khoản nội bộ</span>
        </div>
        <form onSubmit={login}>
          <FormField label="Email">
            <span className="input-icon">
              <Mail size={18} />
              <input
                required
                type="email"
                autoComplete="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="email@congty.com"
              />
            </span>
          </FormField>
          <FormField label="Mật khẩu">
            <span className="input-icon">
              <LockKeyhole size={18} />
              <input
                required
                type="password"
                minLength={8}
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="••••••••"
              />
            </span>
          </FormField>
          {turnstileRequired && turnstileSiteKey && (
            <LoginTurnstile key={turnstileResetNonce} siteKey={turnstileSiteKey} onToken={setTurnstileToken} />
          )}
          {!turnstileLoaded && <p role="status">Đang tải xác minh bảo mật...</p>}
          {turnstileLoaded && turnstileRequired && !turnstileSiteKey && (
            <p role="alert" className="form-error">Xác minh bảo mật chưa sẵn sàng. Vui lòng tải lại trang để thử lại.</p>
          )}
          {error && <p role="alert" className="form-error">{error}</p>}
          <Button type="submit" disabled={pending || !turnstileLoaded || (turnstileRequired && (!turnstileSiteKey || !turnstileToken))} className="login-button">
            {pending ? "Đang đăng nhập..." : "Đăng nhập"}
          </Button>
        </form>
        <small className="login-note">
          <ShieldCheck size={15} /> Thông tin đăng nhập của bạn luôn được bảo
          vệ.
        </small>
      </main>
    </div>
  );
}
